import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import heroImage from "@/assets/hero-image.jpg";
import madridSkyline from "@/assets/madrid-skyline-bg.jpg";

// Custom event to open chatbot
export const openChatbotEvent = new CustomEvent('openChatbot');
const HeroSection = () => {
  const [bgLoaded, setBgLoaded] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Preload background image
  useEffect(() => {
    const img = new Image();
    img.src = madridSkyline;
    img.onload = () => setBgLoaded(true);
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="relative min-h-[100svh] flex items-center pt-16 md:pt-20 pb-8 md:pb-0 overflow-hidden"
    >
      {/* Base Dark Background */}
      <div className="absolute inset-0 bg-primary" />
      
      {/* Background Image - Skyline with lazy load */}
      <motion.div 
        className={`absolute inset-0 transition-opacity duration-1000 ${bgLoaded ? 'opacity-50' : 'opacity-0'}`}
        initial={{ scale: 1.1 }}
        animate={{ scale: bgLoaded ? 1 : 1.1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        style={{
          backgroundImage: `url(${madridSkyline})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
        aria-hidden="true"
      />
      
      {/* Gradient Overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/50 via-primary/70 to-primary/90" />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" aria-hidden="true">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="container mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-sm font-medium text-primary-foreground/90">
                Diseñado para el sector inmobiliario
              </span>
            </motion.div>

            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground leading-[1.15] mb-4 md:mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Controla los gastos de tu operación inmobiliaria desde un solo lugar
            </motion.h1>

            <motion.p 
              className="text-base sm:text-lg md:text-xl text-primary-foreground/80 mb-6 md:mb-8 max-w-xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Tarjetas empresariales, control por propiedad u obra y conciliación de gastos, diseñado para equipos inmobiliarios.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Button variant="hero" size="xl" asChild>
                <a href="https://app.habituspay.com" target="_blank" rel="noopener noreferrer">
                  Empezar gratis
                  <ArrowRight className="ml-1" />
                </a>
              </Button>
              <Button 
                variant="heroOutline" 
                size="xl"
                onClick={() => window.dispatchEvent(openChatbotEvent)}
              >
                <MessageCircle className="mr-1" size={18} />
                Habla con Habi
              </Button>
            </motion.div>

            {/* Trust Microcopy */}
            <motion.p 
              className="text-sm text-primary-foreground/60 mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              HabitusPay es software. Las tarjetas son emitidas por un proveedor externo regulado.
            </motion.p>
          </div>

          {/* Image with optimized loading */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-primary/50">
              {/* Blur placeholder */}
              <div 
                className={`absolute inset-0 bg-primary/30 backdrop-blur-sm transition-opacity duration-500 ${heroLoaded ? 'opacity-0' : 'opacity-100'}`}
                aria-hidden="true"
              />
              
              {/* Hero image with modern attributes */}
              <picture>
                {/* WebP would go here if we had generated versions */}
                <img 
                  src={heroImage} 
                  alt="HabitusPay Dashboard - Panel de control para gestión de gastos inmobiliarios" 
                  className={`w-full h-auto transition-opacity duration-500 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
                  width={800}
                  height={600}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onLoad={() => setHeroLoaded(true)}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 800px"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
            </div>
            
            {/* Floating Card */}
            <motion.div 
              className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-card rounded-xl p-3 md:p-4 shadow-card-hover hidden sm:block"
              initial={{ opacity: 0, y: 20, x: -20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
            >
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-accent md:w-5 md:h-5">
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xs md:text-sm font-medium text-foreground">Gastos controlados</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground">Tiempo real</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg 
          viewBox="0 0 1440 120" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg" 
          className="w-full"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
